import React, { useEffect, useState } from "react";
import "./styles.css";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  AccordionToggleEventHandler,
  Tab,
  TabList,
} from "@fluentui/react-components";
import apiServices from "../../service";
import OverlayLoader from "../../components/OverlayLoader";
import ASCButton from "../../components/Button";
import { formatedDate, formatedDate2 } from "../../utils";

enum TabValues {
  Client = "tab1",
  Partner = "tab2",
}

const ChangesCP = ({
  selectedReason,
  NextToDataSelection,
  BackToSecond,
  CancelCP,
  ClientPartner,
  partnerID,
  getPartner,
  setGetPartner,
}: {
  selectedReason?: string;
  NextToDataSelection: () => void;
  BackToSecond: () => void;
  CancelCP: () => void;
  ClientPartner: any;
  partnerID: any;
  getPartner: any;
  setGetPartner: React.Dispatch<React.SetStateAction<any>>;
}) => {
  const [selectedTab, setSelectedTab] = useState<TabValues>(TabValues.Client);
  const [openItems, setOpenItems] = useState<string[]>(["1"]);
  const [loading, setLoading] = useState(false);

  const handleToggle: AccordionToggleEventHandler<string> = (_e, data) => {
    setOpenItems(data.openItems as string[]);
  };

  useEffect(() => {
    if (partnerID) {
      const fetch = async () => {
        try {
          setLoading(true);
          const res = await apiServices.ClientPartnerSplit.getClientDetail(
            partnerID
          );
          setGetPartner(res.data.data);
        } catch (err) {
          console.error(err);
        } finally {
          setLoading(false);
        }
      };
      fetch();
    }
  }, [partnerID, setGetPartner]);

  // Helper to decide suffix & history note
  const getSuffixAndNote = (tab: TabValues) => {
    switch (selectedReason) {
      case "1": // Separation
        return {
          suffix: " (Separation)",
          history: "History - Separated",
        };

      case "2": // Death of Client
        return tab === TabValues.Client
          ? {
              suffix: " (Deceased)",
              history: "History - Client Deceased",
            }
          : {
              // Surviving partner's record still gets a suffix -- matches
              // the backend's own LastName suffix for this role/reason.
              suffix: " (Client Deceased)",
              history: "History - Client Deceased",
            };

      case "3": // Death of Partner
        return tab === TabValues.Partner
          ? {
              suffix: " (Deceased)",
              history: "History - Partner Deceased",
            }
          : {
              // Surviving client's record still gets a suffix -- matches
              // the backend's own LastName suffix for this role/reason.
              suffix: " (Partner Deceased)",
              history: "History - Partner Deceased",
            };

      default:
        return { suffix: "", history: "" };
    }
  };

  const clientNote = getSuffixAndNote(TabValues.Client);
  const partnerNote = getSuffixAndNote(TabValues.Partner);

  // history should still depend on active tab
  const history =
    selectedTab === TabValues.Client ? clientNote.history : partnerNote.history;

  const suffix =
    selectedTab === TabValues.Client ? clientNote.suffix : partnerNote.suffix;

  const currentPerson =
    selectedTab === TabValues.Client ? ClientPartner : getPartner;
  const isClientTab = selectedTab === TabValues.Client;

  if (!currentPerson) return null;

  const nameAddr = currentPerson.NameAndAddress;
  const admin = currentPerson.Administration;

  return (
    <>
      <OverlayLoader isLoading={loading} />

      <div className="container">
        <div className="sub-heading">
          <h2 className="headings">Changed Client/Partner Details</h2>
          <p className="description">
            Client-Assyst will make these changes to the original client/partner
            records and add a new Address record to the database.
          </p>

          <TabList
            selectedValue={selectedTab}
            onTabSelect={(_e, data) => setSelectedTab(data.value as TabValues)}
          >
            <Tab value={TabValues.Client}>
              <div className="tab-sub-title">
                <p>
                  {ClientPartner?.NameAndAddress?.first_names}{" "}
                  {ClientPartner?.NameAndAddress?.last_name}
                  <br />
                  {clientNote.suffix}
                </p>
              </div>
            </Tab>

            <Tab value={TabValues.Partner}>
              <div className="tab-sub-title">
                <p>
                  {getPartner?.NameAndAddress?.first_names}{" "}
                  {getPartner?.NameAndAddress?.last_name}
                  <br />
                  {partnerNote.suffix}
                </p>
              </div>
            </Tab>
          </TabList>

          <div className="tab-section">
            <div className="tab-heading">
              <div></div>
              <div>
                <strong>Current</strong>
              </div>
              <div>
                <strong>Will Change To</strong>
              </div>
            </div>

            <Accordion
              openItems={openItems}
              onToggle={handleToggle}
              multiple
              collapsible
              style={{ maxHeight: "70vh", overflowY: "auto" }}
            >
              {/* Altered Name & Address */}
              <AccordionItem value="1">
                <AccordionHeader>
                  <strong>Altered Name & Address</strong>
                </AccordionHeader>
                <AccordionPanel>
                  <div className="border-section">
                    <div className="border-inner-style">
                      {/* First Name */}
                      <div className="tab-content">
                        <div className="title-section">First Name</div>
                        <div>{nameAddr?.first_names}</div>
                        <div>{nameAddr?.first_names}</div>
                      </div>

                      {/* Last Name with suffix */}
                      <div className="tab-content">
                        <div className="title-section">Last Name</div>
                        <div>{nameAddr?.last_name}</div>
                        <div>
                          {nameAddr?.last_name}
                          {suffix}
                        </div>
                      </div>

                      {/* Address fields – old address becomes "**DO NOT USE**" */}
                      {[
                        "address_name",
                        "address_1",
                        "address_2",
                        "address_3",
                        "town",
                        "county",
                        "country",
                      ].map((key) => (
                        <div className="tab-content" key={key}>
                          {key === "address_name" && (
                            <div className="title-section">Address Name</div>
                          )}
                          {key === "address_1" && (
                            <div className="title-section">Street Address</div>
                          )}
                          {key === "address_2" && (
                            <div className="title-section"></div>
                          )}
                          {key === "address_3" && (
                            <div className="title-section"></div>
                          )}
                          {key === "town" && (
                            <div className="title-section">Town</div>
                          )}
                          {key === "county" && (
                            <div className="title-section">County</div>
                          )}
                          {key === "country" && (
                            <div className="title-section">Country</div>
                          )}
                          <div>{nameAddr?.[key] || ""}</div>
                          <div>**DO NOT USE**</div>
                        </div>
                      ))}

                      <div className="tab-content">
                        <div className="title-section">Postcode</div>
                        <div>{nameAddr?.postcode}</div>
                        <div>**********</div>
                      </div>
                      <div className="tab-content">
                        <div className="title-section">Date Moved In</div>
                        <div>{formatedDate(nameAddr?.address_start_date)}</div>
                        <div>{formatedDate(nameAddr?.address_start_date)}</div>
                      </div>
                      <div className="tab-content">
                        <div className="title-section">Saluation</div>
                        <div>{nameAddr?.saluation}</div>
                        <div>{nameAddr?.saluation}</div>
                      </div>

                      {/* Joint Address Name with history note */}
                      {isClientTab && nameAddr?.joint_salutation && (
                        <div className="tab-content">
                          <div className="title-section">Joint Salutation</div>
                          <div>{nameAddr.joint_salutation}</div>
                          <div>{nameAddr.joint_salutation}</div>
                        </div>
                      )}
                      {isClientTab && nameAddr?.joint_address_label && (
                        <div className="tab-content">
                          <div className="title-section">
                            Joint Address Name
                          </div>
                          <div>{nameAddr.joint_address_label}</div>
                          <div>{nameAddr.joint_address_label}</div>
                        </div>
                      )}

                      {/* Client Type Section */}
                    </div>
                  </div>
                </AccordionPanel>
              </AccordionItem>

              {/* Previous Address Record */}
              {isClientTab && (
                <AccordionItem value="2">
                  <AccordionHeader>
                    <strong>Altered Client Type</strong>
                  </AccordionHeader>
                  <AccordionPanel>
                    <div className="border-section">
                      <div className="border-inner-style">
                        <div className="tab-content">
                          <div className="title-section">Client Type</div>
                          <div>{admin?.client_type || "-"}</div>
                          <div>{history}</div>
                        </div>
                        <div className="tab-content">
                          <div className="title-section">Other Client Type</div>
                          <div>{admin?.fsa_client_type || ""}</div>
                          <div>{admin?.client_type || ""}</div>
                        </div>
                      </div>
                    </div>
                  </AccordionPanel>
                </AccordionItem>
              )}
              <AccordionItem value="3">
                <AccordionHeader>
                  <strong>
                    Original address to be added to the Address category
                  </strong>
                </AccordionHeader>
                <AccordionPanel>
                  <div className="border-section">
                    <div className="border-inner-style">
                      <div className="tab-content">
                        <div className="title-section">Address Type</div>
                        <div></div>
                        <div>
                          {isClientTab ? "Client" : "Partner"} (Previous
                          Address)
                        </div>
                      </div>
                      <div className="tab-content">
                        <div className="title-section">Street Address</div>
                        <div></div>
                        <div>{currentPerson?.NameAndAddress?.address_1}</div>
                      </div>
                      <div className="tab-content">
                        <div className="title-section"></div>
                        <div></div>
                        <div>{currentPerson?.NameAndAddress?.address_2}</div>
                      </div>
                      <div className="tab-content">
                        <div className="title-section"></div>
                        <div></div>
                        <div>{currentPerson?.NameAndAddress?.address_3}</div>
                      </div>
                      <div className="tab-content">
                        <div className="title-section">Town</div>
                        <div></div>
                        <div>{currentPerson?.NameAndAddress?.town}</div>
                      </div>
                      <div className="tab-content">
                        <div className="title-section">County</div>
                        <div></div>
                        <div>{currentPerson?.NameAndAddress?.county}</div>
                      </div>
                      <div className="tab-content">
                        <div className="title-section">Country</div>
                        <div></div>
                        <div>{currentPerson?.NameAndAddress?.country}</div>
                      </div>
                      <div className="tab-content">
                        <div className="title-section">Postcode</div>
                        <div></div>
                        <div>{currentPerson?.NameAndAddress?.postcode}</div>
                      </div>
                      <div className="tab-content">
                        <div className="title-section">Telephone</div>
                        <div></div>
                        <div>{currentPerson?.NameAndAddress?.telephone}</div>
                      </div>
                      <br />
                      <div className="tab-content">
                        <div className="title-section">Home Phone</div>
                        <div></div>
                        <div>{currentPerson?.Contact?.home_phone}</div>
                      </div>
                      <div className="tab-content">
                        <div className="title-section">Work Phone</div>
                        <div></div>
                        <div>{currentPerson?.Contact?.work_phone}</div>
                      </div>
                      <div className="tab-content">
                        <div className="title-section">Mobile</div>
                        <div></div>
                        <div>{currentPerson?.Contact?.mobile}</div>
                      </div>
                      <div className="tab-content">
                        <div className="title-section">Email</div>
                        <div></div>
                        <div>{currentPerson?.Contact?.email}</div>
                      </div>
                      <div className="tab-content">
                        <div className="title-section">Work Email</div>
                        <div></div>
                        <div>{currentPerson?.Contact?.work_email}</div>
                      </div>

                      <br />
                      <div className="tab-content">
                        <div className="title-section">Date Moved In</div>
                        <div></div>
                        <div>
                          {formatedDate(
                            currentPerson?.NameAndAddress?.address_start_date
                          )}
                        </div>
                      </div>
                      {/* Add other address lines, phone, email as needed */}
                    </div>
                  </div>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>

      <div className="buttons">
        <ASCButton
          shape="rounded"
          size="medium"
          onClick={BackToSecond}
          label="Back"
        />
        <ASCButton
          shape="rounded"
          size="medium"
          appearance="primary"
          className="asc-button-primary"
          onClick={NextToDataSelection}
          label="Next"
        />
        <ASCButton
          shape="rounded"
          size="medium"
          onClick={CancelCP}
          label="Cancel"
        />
      </div>
    </>
  );
};

export default ChangesCP;
